// @ts-nocheck
/** @typedef {import('../types/api').ProductImage} ProductImage */

import { AlertTriangle, ArrowDown, ArrowUp, Plus, Sparkles, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { adminProductAiService, adminProductImageService } from '../services/adminProductExtrasService';
import api from '../utils/apiClient';
import FileUpload from './FileUpload';
import { FocalPointButton, focalToObjectPosition, toFocal } from './ui/FocalPointPicker';
import ImagePreviewButton from './ui/ImagePreviewButton';

// Where the storefront crops product images (the PDP gallery shows them uncropped).
const PRODUCT_IMAGE_PREVIEWS = [
  { label: 'Product card 4:5', ratio: '4 / 5' },
  { label: 'Quick view / wishlist 3:4', ratio: '3 / 4' },
];

const ProductImagesManager = ({
  productId,
  productName,
  images,
  onImagesChange,
  featuredImageUrl,
  setFeaturedImage,
  variants = [],
}) => {
  const [showUpload, setShowUpload] = useState(false);
  const [generatingAltTextId, setGeneratingAltTextId] = useState(null);
  const [altTextError, setAltTextError] = useState('');

  const handleAltTextChange = (imageId, value) => {
    onImagesChange(images.map((img) => (img.id === imageId ? { ...img, alt_text: value } : img)));
  };

  const handleGenerateAltText = async (image) => {
    if (!productName?.trim()) {
      setAltTextError('Add a product name first.');
      return;
    }
    setAltTextError('');
    setGeneratingAltTextId(image.id);
    try {
      const result = await adminProductAiService.generateAltText({
        image_url: image.url,
        name: productName,
      });
      handleAltTextChange(image.id, result.alt_text || '');
    } catch (err) {
      setAltTextError(err.message || 'Failed to generate alt text');
    } finally {
      setGeneratingAltTextId(null);
    }
  };

  // Persisted images save immediately (like Set Featured); not-yet-saved uploads keep the
  // focal point in local state and ProductEditModal sends it with the create payload.
  const handleSaveFocal = async (image, focal) => {
    if (productId && !image.id.toString().startsWith('temp_')) {
      try {
        await adminProductImageService.updateProductImage(productId, image.id, { focal_x: focal.x, focal_y: focal.y });
      } catch (err) {
        console.error('Failed to save focal point:', err);
        throw err; // keeps the picker open
      }
    }
    onImagesChange(images.map((img) => (img.id === image.id ? { ...img, focal_x: focal.x, focal_y: focal.y } : img)));
  };

  const handleImagesUploaded = (uploadedFiles) => {
    const newImages = uploadedFiles.map((file, index) => ({
      id: `temp_${Date.now()}_${index}`,
      url: file.url,
      public_id: file.public_id,
      alt_text: '',
      display_order: images.length + index,
    }));
    onImagesChange([...images, ...newImages]);
    setShowUpload(false);
  };

  const handleRemoveImage = async (imageId) => {
    const image = images.find((img) => img.id === imageId);
    const dependentVariants = image ? variants.filter((v) => v.image_url && v.image_url === image.url) : [];

    const confirmMessage =
      dependentVariants.length > 0
        ? `This photo is set as the color photo for: ${dependentVariants
            .map((v) => [v.size, v.color].filter(Boolean).join(' / ') || 'a variant')
            .join(
              ', ',
            )}. It'll stop showing up here to pick again, though those variants will keep working with it until you change their photo. Delete anyway?`
        : 'Delete this image?';

    if (!window.confirm(confirmMessage)) return;

    if (productId && !imageId.toString().startsWith('temp_')) {
      try {
        await adminProductImageService.deleteProductImage(productId, imageId);
      } catch (err) {
        console.error('Failed to delete image:', err);
        alert('Failed to delete image');
        return;
      }
    }

    const updatedImages = images.filter((img) => img.id !== imageId);
    updatedImages.forEach((img, idx) => {
      img.display_order = idx;
    });
    onImagesChange(updatedImages);
  };

  const handleSetFeatured = async (imageId, imageUrl) => {
    if (productId) {
      try {
        await api.patch(`/api/admin/products/${productId}/images/${imageId}/set-featured`);
      } catch (error) {
        console.error('Error setting featured image:', error);
        return;
      }
    }
    setFeaturedImage(imageUrl);
  };

  const handleReorder = (imageId, direction) => {
    const currentIndex = images.findIndex((img) => img.id === imageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    const updatedImages = [...images];
    [updatedImages[currentIndex], updatedImages[newIndex]] = [updatedImages[newIndex], updatedImages[currentIndex]];

    updatedImages.forEach((img, idx) => {
      img.display_order = idx;
    });

    onImagesChange(updatedImages);
  };

  const hasFeaturedImage = images.some((img) => img.url === featuredImageUrl);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium">Product Images</h3>
          <p className="text-sm text-muted-foreground">
            {images.length} image(s) • {hasFeaturedImage ? 'Featured image set' : 'First image shown on cards & PDP'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} />
          {showUpload ? 'Cancel' : 'Upload Images'}
        </button>
      </div>

      {/* File Upload */}
      {showUpload && (
        <div className="border-2 border-dashed border-input rounded-lg p-4 bg-muted/50">
          <FileUpload
            onUploadComplete={handleImagesUploaded}
            uploadType="product-image"
            accept="image/*"
            multiple={true}
            maxFiles={10}
            existingFiles={[]}
          />
        </div>
      )}

      {/* Image Gallery */}
      {images.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <p className="text-muted-foreground">No images uploaded yet</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Click "Upload Images" to add product photos</p>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
            <p>
              Image #1 (top-left) is shown on product cards and the detail page. Use arrows to reorder. Optionally set a{' '}
              <strong>Featured Image</strong> — the large lifestyle shot for this product on the homepage's "Outfit
              Inspiration" slider. Without one, that slider falls back to image #2.
            </p>
          </div>
          {altTextError && <p className="text-xs text-red-600 dark:text-red-400">{altTextError}</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((image, index) => {
              const isFeatured = image.url === featuredImageUrl;
              return (
                <div key={image.id} className="space-y-1">
                  <div
                    className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                      isFeatured
                        ? 'border-amber-400 shadow-lg ring-2 ring-amber-100'
                        : index === 0
                          ? 'border-blue-400'
                          : 'border-border hover:border-input'
                    }`}
                  >
                    <ImagePreviewButton src={image.url} alt={image.alt_text || `Product image ${index + 1}`} />
                    <FocalPointButton
                      src={image.url}
                      value={toFocal(image.focal_x, image.focal_y)}
                      previews={PRODUCT_IMAGE_PREVIEWS}
                      onSave={(f) => handleSaveFocal(image, f)}
                      className={`absolute top-2 right-12 p-1.5 rounded-full transition-colors opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 z-10 ${
                        image.focal_x != null
                          ? 'bg-card text-foreground hover:bg-accent'
                          : 'bg-black/60 hover:bg-black/80 text-white'
                      }`}
                    />
                    <img
                      src={image.url}
                      alt={image.alt_text || `Product image ${index + 1}`}
                      style={{ objectPosition: focalToObjectPosition(toFocal(image.focal_x, image.focal_y)) }}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="300"%3E%3Crect fill="%23f3f4f6" width="300" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />

                    {/* Featured Badge */}
                    {isFeatured && (
                      <div className="absolute top-2 left-2 bg-amber-400 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                        <Star size={12} fill="white" />
                        Featured
                      </div>
                    )}

                    {/* First image indicator */}
                    {index === 0 && !isFeatured && (
                      <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                        Card
                      </div>
                    )}

                    {/* Image Order */}
                    <div className="absolute top-2 right-2 bg-primary bg-opacity-70 text-primary-foreground text-xs px-2 py-1 rounded-md">
                      #{index + 1}
                    </div>

                    {/* Actions */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-3 space-y-1">
                      <div className="flex gap-1">
                        {!isFeatured && (
                          <button
                            type="button"
                            onClick={() => handleSetFeatured(image.id, image.url)}
                            className="flex-1 text-white text-xs py-2 bg-amber-500 hover:bg-amber-600 rounded-md transition-colors font-medium flex items-center justify-center gap-1"
                            title="Set as featured image"
                          >
                            <Star size={11} />
                            Set Featured
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(image.id)}
                          className="px-3 text-white text-xs py-2 bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                          title="Delete image"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Reorder buttons */}
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => handleReorder(image.id, 'up')}
                          disabled={index === 0}
                          className="flex-1 text-primary-foreground text-xs py-1 bg-primary hover:bg-muted-foreground rounded-md disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                          title="Move up"
                        >
                          <ArrowUp size={12} /> Move Up
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReorder(image.id, 'down')}
                          disabled={index === images.length - 1}
                          className="flex-1 text-primary-foreground text-xs py-1 bg-primary hover:bg-muted-foreground rounded-md disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                          title="Move down"
                        >
                          <ArrowDown size={12} /> Move Down
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      value={image.alt_text || ''}
                      onChange={(e) => handleAltTextChange(image.id, e.target.value)}
                      placeholder="Alt text (for accessibility & SEO)"
                      maxLength={250}
                      className="flex-1 min-w-0 text-xs px-2 py-1.5 border border-border rounded-md focus:outline-none focus:border-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => handleGenerateAltText(image)}
                      disabled={generatingAltTextId === image.id}
                      title="Generate alt text with AI"
                      className="shrink-0 p-1.5 border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles size={12} className={generatingAltTextId === image.id ? 'animate-pulse' : ''} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductImagesManager;
